import { useState, useMemo, useEffect, useRef } from "react";
import ContentHeader from "../../components/app/headers/ContentHeader";
import { NavLang } from "../../components/app/headers/layout";
import ItemList from "../../components/app/ItemList";
import Navbar from "../../components/app/navbars/Navbar";
import ArticlePublicationCard from "../../components/cards/ArticlePublicationCard";
import ContentFilterButton from "../../components/filters/ContentFilterButton";
import SpecialtyFilterPanel from "../../components/filters/SpecialtyFilterPanel";
import SubSpecialtyFilterPanel from "../../components/filters/SubSpecialtyFilterPanel";
import NoContent from "../../components/NoContent";
import SkeletonPublicationCard from "../../components/skeleton/SkeletonPublicationCard";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { usePageScroll } from "../../domains/app/hooks/usePageScroll";
import { getStoredContentFilters } from "../../domains/app/utils/contentFilters";
import { getItemTranslation } from "../../domains/app/utils/getItemTranslation";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import { gaEventViewItemListArticle } from "../../tools/analytics/domains/article.analytics";
import { useArticleSwipe } from "../../features/article/hooks/useArticleSwipe";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { useTranslation } from "react-i18next";
import { useApiInfiniteQuery, useApiQuery } from "../../services/api";

const articleListLimit = 20;

export default function ArticlesListPage(props: { slug?: string }) {
  const slug = props.slug ?? null;
  const { t } = useTranslation();
  const { contentLang } = useActiveLang();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { savePageScrollY, restorePageScrollY } = usePageScroll(scrollRef);
  const specialtiesQuery = useApiQuery("/webapp/public/tags/parents", {
    durable: true,
  });
  const medicalSpecialties =
    specialtiesQuery.state === "success" ? specialtiesQuery.body : [];

  // Find specialty from slug if present
  const specialtyFromSlug = useMemo(() => {
    if (slug === null) return null;
    return medicalSpecialties.find((s) => s.uid === slug) ?? null;
  }, [slug, medicalSpecialties]);

  const [specialtyFilters, setSpecialtyFilters] = useState<string[]>(
    specialtyFromSlug === null
      ? (getStoredContentFilters().specialtyIds ?? [])
      : [specialtyFromSlug.id]
  );
  const [subSpecialtyFilters, setSubSpecialtyFilters] = useState<string[]>(
    getStoredContentFilters().subSpecialtyIds
  );
  const [isFilterPanelVisible, setFilterPanelVisible] = useState(false);
  const isAllSpecialties = slug === null;

  // Update filters when slug changes
  useEffect(() => {
    if (slug === null) {
      setSpecialtyFilters(getStoredContentFilters().specialtyIds ?? []);
      setSubSpecialtyFilters(getStoredContentFilters().subSpecialtyIds ?? []);
    } else {
      if (specialtyFromSlug !== null) {
        setSpecialtyFilters([specialtyFromSlug.id]);
        setSubSpecialtyFilters([]);
      }
    }
  }, [specialtyFromSlug, slug]);

  const articleListQuery = useApiInfiniteQuery("/webapp/public/articles", {
    query: {
      limit: articleListLimit,
      sort: "default",
      language: contentLang,
      medical_specialties:
        specialtyFilters.length > 0 ? specialtyFilters.join(",") : undefined,
      tags:
        subSpecialtyFilters.length > 0
          ? subSpecialtyFilters.join(",")
          : undefined,
    },
  });
  const displayedArticles =
    articleListQuery.state === "success" ? articleListQuery.docs : [];
  const displayedArticlesIds = displayedArticles
    .map((article) => article.id)
    .join(",");
  const isLoading = articleListQuery.state === "pending";
  const { setSwipeList, setSwipeIndex } = useArticleSwipe();

  useEffectOnce(() => {
    if (displayedArticles.length > 0) {
      restorePageScrollY();
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  useEffectOnce(() => {
    if (displayedArticles.length > 0) {
      gaEventViewItemListArticle({
        articles: displayedArticles,
        listName: AnalyticsItemList.HOME,
      });
    }
  }, [displayedArticlesIds]);

  function handleChangeFilters() {
    if (isAllSpecialties) {
      setSpecialtyFilters(getStoredContentFilters().specialtyIds);
    } else {
      setSubSpecialtyFilters(getStoredContentFilters().subSpecialtyIds);
    }
  }

  function handleScrollEnd() {
    if (
      articleListQuery.state === "success" &&
      articleListQuery.canFetchNextPage
    )
      articleListQuery.fetchNextPage();
  }

  return (
    <ScrollableLayout
      scrollRef={scrollRef}
      headerClassName="bg-white/90 backdrop-blur-md"
      header={
        <ContentHeader
          transparent
          title={
            specialtyFromSlug === null
              ? t("Publications")
              : getItemTranslation(specialtyFromSlug)
          }
          actions={<NavLang />}
        />
      }
      footer={<Navbar />}
      onScrollEnd={handleScrollEnd}
    >
      <div className="bg-primary-yellow min-h-full">
        <ContentFilterButton
          active={
            slug === null
              ? specialtyFilters.length > 0
              : subSpecialtyFilters.length > 0
          }
          onClick={() => {
            setFilterPanelVisible(true);
          }}
        />
        {isLoading ? (
          <ItemList>
            {Array.from({ length: 10 }).map((_, index) => (
              <SkeletonPublicationCard
                showImage
                key={`${index}--skeleton-card`}
                showDescription
              />
            ))}
          </ItemList>
        ) : displayedArticles.length === 0 ? (
          <NoContent
            specialty={
              specialtyFromSlug === null
                ? t("your search")
                : getItemTranslation(specialtyFromSlug)
            }
          />
        ) : (
          <ItemList>
            {displayedArticles.map((article, index) => (
              <ArticlePublicationCard
                roomId={null}
                key={article.id}
                article={article}
                analyticsListName={AnalyticsItemList.HOME}
                recommendation={null}
                onClick={() => {
                  setSwipeList(displayedArticles);
                  setSwipeIndex(index);
                  savePageScrollY();
                }}
                className={null}
                showImage={true}
              />
            ))}
          </ItemList>
        )}
      </div>

      {isAllSpecialties ? (
        <SpecialtyFilterPanel
          room={null}
          isOpen={isFilterPanelVisible}
          onClose={() => {
            setFilterPanelVisible(false);
          }}
          onSave={() => {
            handleChangeFilters();
          }}
        />
      ) : specialtyFromSlug === null ? null : (
        <SubSpecialtyFilterPanel
          specialty={specialtyFromSlug}
          room={null}
          isOpen={isFilterPanelVisible}
          onClose={() => {
            setFilterPanelVisible(false);
          }}
          onSave={() => {
            handleChangeFilters();
          }}
        />
      )}
    </ScrollableLayout>
  );
}
